// Throttle UI updates to 100ms
// Price History Configuration
import type { PriceHistoryTimeframe } from "@/hooks/polymarket/types";

/**
 * Polymarket Constants
 * Contract addresses, ABIs, and API configuration for Polygon network
 */

export const POLYGON_CHAIN_ID = 137;

// API URLs
export const CLOB_API_URL = "https://clob.polymarket.com";
export const POLYMARKET_DATA_API_URL = "https://data-api.polymarket.com";
export const RELAYER_URL = "https://relayer-v2.polymarket.com/";
export const POLYMARKET_GEOBLOCK_URL = "https://polymarket.com/api/geoblock";
export const POLYMARKET_PROFILE_URL = (address: string) => `https://polymarket.com/${address}`;
export const POLYMARKET_GEOBLOCK_FAQ_URL = "https://docs.polymarket.com/polymarket-learn/FAQ/geoblocking";

// RPC URLs
export const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || "https://polygon-rpc.com";

// Remote signing
export const REMOTE_SIGNING_URL = () =>
  typeof window !== "undefined" ? `${window.location.origin}/api/polymarket/sign` : "/api/polymarket/sign";

// Session storage
export const SESSION_STORAGE_KEY = "polymarket_trading_session";

// Token Addresses on Polygon
export const USDC_ADDRESS = "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174"; // USDC (Bridged)
export const USDC_DECIMALS = 6; // USDC uses 6 decimal places
export const USDC_SYMBOL = "USDC.e";
export const CTF_EXCHANGE_ADDRESS = "0x4bFb41d5B3570DeFd03C39a9A4D8dE6Bd8B8982E"; // CTF Exchange
export const CONDITIONAL_TOKENS_ADDRESS = "0x4D97DCd97eC945f40cF65F87097ACe5EA0476045"; // Conditional Tokens
export const CTF_CONTRACT_ADDRESS = CONDITIONAL_TOKENS_ADDRESS; // CTF Contract (Conditional Tokens)
export const NEG_RISK_CTF_EXCHANGE_ADDRESS = "0xC5d563A36AE78145C45a50134d48A1215220f80a"; // Neg Risk CTF Exchange
export const NEG_RISK_ADAPTER_ADDRESS = "0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296"; // Neg Risk Adapter

// Pagination
export const DEFAULT_EVENTS_PAGE_SIZE = 20;

// WebSocket URLs
export const POLYMARKET_WS_MARKET_URL = "wss://ws-subscriptions-clob.polymarket.com/ws/market";
export const POLYMARKET_WS_USER_URL = "wss://ws-subscriptions-clob.polymarket.com/ws/user";

// WebSocket Configuration
export const WS_PING_INTERVAL_MS = 10_000; // Polymarket requires PING every 10 seconds
export const WS_RECONNECT_MAX_ATTEMPTS = 10;
export const WS_RECONNECT_MAX_DELAY_MS = 30_000;
export const WS_THROTTLE_DELAY_MS = 100; // Throttle UI updates to 100ms

export const PRICE_HISTORY_TIMEFRAMES = {
  "1h": { interval: "1h", fidelity: 1 },
  "24h": { interval: "1d", fidelity: 5 },
  "7d": { interval: "1w", fidelity: 60 },
  "30d": { interval: "1m", fidelity: 360 },
  "all": { interval: "max", fidelity: 1440 },
} as const satisfies Record<PriceHistoryTimeframe, { interval: string; fidelity: number }>;

// Balance Configuration
export const BALANCE_STALE_TIME_MS = 30_000; // 30 seconds

// Chart Colors for multi-outcome price charts
export const CHART_OUTCOME_COLORS = [
  "#4A90D9", // Blue
  "#F5A623", // Orange
  "#7ED321", // Green
  "#D0021B", // Red
  "#9013FE", // Purple
  "#50E3C2", // Teal
  "#B8E986", // Lime
  "#F8E71C", // Yellow
] as const;

// UI Labels
export const EMPTY_VALUE_PLACEHOLDER = "--";

// Max allowance for approvals (2^256 - 1)
export const MAX_ALLOWANCE = BigInt("115792089237316195423570985008687907853269984665640564039457584007913129639935");

// Contract ABIs (minimal - only what we need for approvals)
export const ERC20_ABI = [
  {
    constant: false,
    inputs: [
      { name: "_spender", type: "address" },
      { name: "_value", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "_owner", type: "address" },
      { name: "_spender", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    type: "function",
  },
] as const;

export const CONDITIONAL_TOKENS_ABI = [
  {
    constant: false,
    inputs: [
      { name: "operator", type: "address" },
      { name: "approved", type: "bool" },
    ],
    name: "setApprovalForAll",
    outputs: [],
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "owner", type: "address" },
      { name: "operator", type: "address" },
    ],
    name: "isApprovedForAll",
    outputs: [{ name: "", type: "bool" }],
    type: "function",
  },
] as const;
